/**
 * Author: Romain Hapi Sr.
 * Version: 1.0
 * Date: 2026-09-11
 * Project: Prenim Digital
 * Copyright: Prenim UK
 */
import React, { useCallback, useEffect, useState } from "react";
import { ArrowUp } from "lucide-react";

/**
 * Bouton de retour en haut de page, partage par les deux sites.
 *
 * Les pages longues — un document juridique, la liste des projets, l'accueil et
 * sa section FaDel — obligeaient a remonter au doigt sur plusieurs ecrans pour
 * retrouver la navigation, qui est en tete de page.
 *
 * Le bouton n'apparait qu'une fois la page descendue d'un ecran et demi : plus
 * tot, il masquerait du contenu sans rendre service, puisque la barre de
 * navigation est encore visible.
 *
 * `variant` porte la couleur d'accent du site hote — bleu au UK, or au Tchad —
 * plutot qu'une teinte neutre qui jurerait avec les deux chartes.
 */
const BackToTop = ({ variant = "uk", lang = "fr" }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // `passive: true` : le gestionnaire ne bloque jamais le defilement, ce qui
    // compte sur les terminaux modestes vises par le site Tchad.
    const onScroll = () => setVisible(window.scrollY > window.innerHeight * 1.5);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  const toTop = useCallback(() => {
    // Le defilement anime devient penible pour qui a demande la reduction des
    // animations : on saute alors directement en haut.
    const reduced =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: reduced ? "auto" : "smooth" });
  }, []);

  const label = LABELS[lang] || LABELS.fr;

  const accent =
    variant === "tchad"
      ? "bg-amber-500 text-slate-950 hover:bg-amber-400 focus-visible:outline-amber-400"
      : "bg-blue-600 text-white hover:bg-blue-500 focus-visible:outline-blue-500";

  return (
    <button
      type="button"
      onClick={toTop}
      data-testid="back-to-top"
      aria-label={label}
      title={label}
      // Le bouton reste monte et s'efface en opacite plutot que d'etre retire
      // du DOM : le fondu peut jouer, et `tabIndex={-1}` le sort de l'ordre de
      // tabulation tant qu'il est invisible. `end-5` et non `right-5`, pour
      // qu'il passe a gauche dans la version arabe, en droite-a-gauche.
      aria-hidden={!visible}
      tabIndex={visible ? 0 : -1}
      className={`fixed bottom-5 end-5 z-40 flex h-12 w-12 items-center justify-center rounded-full
        shadow-lg transition-[opacity,transform] duration-300 focus-visible:outline
        focus-visible:outline-2 focus-visible:outline-offset-2 ${accent} ${
        visible ? "opacity-100" : "pointer-events-none translate-y-3 opacity-0"
      }`}
    >
      <ArrowUp className="h-5 w-5" strokeWidth={2.5} />
    </button>
  );
};

/**
 * Le bouton n'a pas de texte visible : son nom accessible est tout ce qu'un
 * lecteur d'ecran annonce. Il doit donc suivre la langue de la page, sinon il
 * est lu dans une langue etrangere au reste du document.
 */
const LABELS = {
  fr: "Revenir en haut de la page",
  en: "Back to top of page",
  es: "Volver al principio de la página",
  ar: "العودة إلى أعلى الصفحة",
};

export default BackToTop;
